import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import Dialog from '../common/Dialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  createEvent, updateEvent, deleteEvent,
  duplicateEvent, fetchEvents
} from '../../store/slices/eventsSlice';
import { fetchUsers } from '../../store/slices/usersSlice';
import { showToast } from '../utils/Toast';
import { config } from '../../config/config';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#6B7280'];

const PROJECT_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'ready_to_schedule', label: 'Ready to Schedule' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'archived', label: 'Archived' }
];

const normalizeRole = (role = '') => String(role).trim().toLowerCase().replace(/[\s-]+/g, '_');

const endOfDay = (d) => {
  if (!d) return null;
  const copy = new Date(d);
  copy.setHours(23, 59, 59, 999);
  return copy;
};

const normalizeUnavailableWindow = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;

  const endIsMidnight = (
    e.getHours() === 0 &&
    e.getMinutes() === 0 &&
    e.getSeconds() === 0 &&
    e.getMilliseconds() === 0
  );
  if (endIsMidnight) {
    e.setHours(23, 59, 59, 999);
  }

  if (e.getTime() < s.getTime()) {
    e.setHours(23, 59, 59, 999);
  }

  return { start: s, end: e };
};

const rangesOverlap = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && aEnd > bStart;

const EventDialog = ({ isOpen, onClose, event = null, prefill = null }) => {
  const dispatch = useDispatch();
  const role = useSelector((s) => s.auth.user?.profile?.role);
  const currentUserId = useSelector((s) => s.auth.user?._id);
  const { users } = useSelector((s) => s.users);
  const crews = useSelector((s) => s.crews.items);
  const events = useSelector((s) => s.events.events);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [start, setStart] = useState(new Date());
  const [end, setEnd] = useState(new Date(Date.now() + 60 * 60 * 1000));
  const [allDay, setAllDay] = useState(false);
  const [location, setLocation] = useState('');
  const [color, setColor] = useState('');
  const [crewId, setCrewId] = useState('');
  const [crewSearch, setCrewSearch] = useState('');
  const [visibility, setVisibility] = useState('all');
  const [members, setMembers] = useState([]);
  const [labels, setLabels] = useState('');
  const [notifyMembers, setNotifyMembers] = useState(false);

  // Recurrence
  const [recFreq, setRecFreq] = useState('none');
  const [recInterval, setRecInterval] = useState(1);
  const [recWeekdays, setRecWeekdays] = useState([]);
  const [recUntil, setRecUntil] = useState(null);
  const [recCount, setRecCount] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Project picker
  const [projectId, setProjectId] = useState('');
  const [projectMeta, setProjectMeta] = useState(null); // { _id, jobNumber, client?: { name } }
  const [projectQuery, setProjectQuery] = useState('');
  const [projectStatus, setProjectStatus] = useState('active');
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);

  const normalizedUsers = useMemo(
    () => (users || [])
      .filter((u) => u && u._id)
      .map((u) => {
        const firstName = u.profile?.firstName || u.firstName || '';
        const lastName = u.profile?.lastName || u.lastName || '';
        const roleValue = u.profile?.role || u.role || '';
        return {
          ...u,
          firstName,
          lastName,
          roleValue,
          displayName: `${firstName} ${lastName}`.trim() || u.email || 'Unknown User'
        };
      }),
    [users]
  );

  const crewOptions = useMemo(() => {
    const windowStart = new Date(start);
    const windowEnd = new Date(end);
    const hasValidRange = !Number.isNaN(windowStart.getTime()) &&
      !Number.isNaN(windowEnd.getTime()) &&
      windowEnd.getTime() > windowStart.getTime();

    return (crews || []).map((crew) => {
      const base = {
        id: crew._id,
        name: crew.name,
        color: crew.color,
        trades: crew.trades || [],
        people: crew.people || [],
        blocked: false,
        reason: ''
      };
      if (!hasValidRange) return base;

      const manualBlock = (crew.unavailability || []).find((u) => {
        const normalized = normalizeUnavailableWindow(u.start, u.end);
        if (!normalized) return false;
        return rangesOverlap(
          windowStart.getTime(),
          windowEnd.getTime(),
          normalized.start.getTime(),
          normalized.end.getTime()
        );
      });

      if (manualBlock) {
        return {
          ...base,
          blocked: true,
          reason: `${crew.name} is unavailable for this time${manualBlock.reason ? ` (${manualBlock.reason})` : ''}`
        };
      }

      const scheduledBlock = (events || []).find((ev) => {
        const evCrewId = ev?.crew?._id || ev?.crew;
        if (!evCrewId || String(evCrewId) !== String(crew._id)) return false;
        if (event?._id && String(ev._id) === String(event._id)) return false;
        if (!ev.start || !ev.end) return false;
        const s = new Date(ev.start).getTime();
        const e = new Date(ev.end).getTime();
        if (Number.isNaN(s) || Number.isNaN(e)) return false;
        return rangesOverlap(windowStart.getTime(), windowEnd.getTime(), s, e);
      });

      if (scheduledBlock) {
        return {
          ...base,
          blocked: true,
          reason: `${crew.name} is already booked (${scheduledBlock.title || 'scheduled event'})`
        };
      }

      return base;
    });
  }, [crews, events, start, end, event?._id]);

  const selectedCrewOption = useMemo(
    () => crewOptions.find((c) => String(c.id) === String(crewId)) || null,
    [crewOptions, crewId]
  );

  const hasCrewConflict = Boolean(selectedCrewOption?.blocked);

  const filteredCrewOptions = useMemo(() => {
    const q = crewSearch.trim().toLowerCase();
    if (!q) return crewOptions;
    return crewOptions.filter((c) => {
      if ((c.name || '').toLowerCase().includes(q)) return true;
      const tradeHit = (c.trades || []).some((t) => (t.name || '').toLowerCase().includes(q));
      if (tradeHit) return true;
      return (c.people || []).some((p) => {
        if ((p.name || '').toLowerCase().includes(q)) return true;
        return (p.trades || []).some((t) => (t.name || '').toLowerCase().includes(q));
      });
    });
  }, [crewOptions, crewSearch]);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchUsers());
      dispatch(fetchEvents());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (!isOpen) setShowDeleteConfirm(false);
  }, [isOpen]);

  // Debounced project search/filter fetch.
  useEffect(() => {
    if (!isOpen || !projectPickerOpen) return;
    const handle = setTimeout(async () => {
      try {
        setProjectLoading(true);
        const token = localStorage.getItem('token');
        const params = new URLSearchParams({
          page: '1',
          limit: '20',
          sortField: 'createdAt',
          sortOrder: 'desc',
          ...(projectQuery.trim() ? { search: projectQuery.trim() } : {}),
          ...(projectStatus ? { projectStatus } : {})
        });
        const res = await fetch(`${config.apiUrl}/api/projects?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok) setProjectOptions(data.projects || []);
        else setProjectOptions([]);
      } catch (e) {
        setProjectOptions([]);
      } finally {
        setProjectLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [isOpen, projectPickerOpen, projectQuery, projectStatus]);

  useEffect(() => {
    if (!isOpen) return;
    if (event) {
      setTitle(event.title || '');
      setDescription(event.description || '');
      setStart(new Date(event.start));
      setEnd(new Date(event.end));
      setAllDay(!!event.allDay);
      setLocation(event.location || '');
      setColor(event.color || '');
      setCrewId(event.crew?._id || event.crew || '');
      setVisibility(event.visibility || 'all');
      setMembers((event.members || []).map((m) => m._id || m));
      setLabels((event.labels || []).join(', '));
      setNotifyMembers(event.notifyMembers === true);
      const r = event.recurrence || {};
      setRecFreq(r.freq || 'none');
      setRecInterval(r.interval || 1);
      setRecWeekdays(r.byWeekday || []);
      setRecUntil(r.until ? new Date(r.until) : null);
      setRecCount(r.count || '');
      const projObj = event.project && typeof event.project === 'object' ? event.project : null;
      setProjectId(projObj?._id || event.project || '');
      setProjectMeta(projObj);
    } else {
      setTitle(prefill?.title || '');
      setDescription('');
      setStart(prefill?.start ? new Date(prefill.start) : new Date());
      setEnd(prefill?.end ? new Date(prefill.end) : new Date(Date.now() + 60 * 60 * 1000));
      setAllDay(Boolean(prefill?.allDay));
      setLocation(prefill?.location || '');
      setColor('');
      setCrewId(prefill?.crewId || '');
      setVisibility('all');
      setMembers(prefill?.members || []);
      setLabels('');
      setNotifyMembers(false);
      setRecFreq('none');
      setRecInterval(1);
      setRecWeekdays([]);
      setRecUntil(null);
      setRecCount('');
      setProjectId(prefill?.projectId || '');
      setProjectMeta(prefill?.projectMeta || null);
    }
    setProjectQuery('');
    setProjectStatus('active');
    setProjectPickerOpen(false);
    setCrewSearch('');
  }, [event, isOpen, prefill]);

  // Helper: build a combined title from project + crew.
  const buildProjectCrewTitle = (proj, currentCrewId) => {
    if (!proj) return '';
    const clientName = proj.client?.name || '';
    const base = [proj.jobNumber, clientName].filter(Boolean).join(' — ');
    if (!base) return '';
    if (currentCrewId) {
      const crew = (crews || []).find((c) => String(c._id) === String(currentCrewId));
      if (crew?.name) return `${base} (${crew.name})`;
    }
    return base;
  };

  // When crew changes while a project is linked, rebuild title to include crew name.
  useEffect(() => {
    if (!isOpen || !projectMeta) return;
    const newTitle = buildProjectCrewTitle(projectMeta, crewId);
    if (newTitle) setTitle(newTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crewId]);

  const toggleMember = (id) => {
    setMembers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleSelectProject = (p) => {
    setProjectId(p._id);
    setProjectMeta(p);
    setProjectPickerOpen(false);

    // When a project is selected, force the event to be all-day.
    setAllDay(true);
    const s = new Date(start); s.setHours(0, 0, 0, 0); setStart(s);
    const en = new Date(end); en.setHours(23, 59, 59, 999); setEnd(en);

    // Auto-fill the project address into Location if Location is empty.
    const addr = p.address || {};
    const formattedAddr = [addr.street, addr.city, addr.state, addr.zipCode]
      .filter(Boolean)
      .join(', ');
    if (formattedAddr && !location.trim()) {
      setLocation(formattedAddr);
    }

    // Always set the title to the project info + crew name when a project is linked.
    const suggestedTitle = buildProjectCrewTitle(p, crewId);
    if (suggestedTitle) {
      setTitle(suggestedTitle);
    }
  };

  const buildPayload = () => {
    const normalizedInterval = Math.max(1, Number(recInterval) || 1);
    const startWeekday = new Date(start).getDay();
    const recurrenceByWeekday = recFreq === 'weekly'
      ? (recWeekdays.length > 0 ? recWeekdays : [startWeekday])
      : [];

    // For allDay events, strip the time components so the event is timezone-safe:
    // start = local midnight of the chosen day, end = local 23:59:59 of the chosen day.
    let saveStart = start;
    let saveEnd = end;
    if (allDay) {
      const s = new Date(start);
      s.setHours(0, 0, 0, 0);
      saveStart = s;
      const e = new Date(end);
      e.setHours(23, 59, 59, 999);
      // Ensure end is at least on the same day as start
      if (e.getTime() < s.getTime()) e.setTime(s.getTime() + 24 * 60 * 60 * 1000 - 1);
      saveEnd = e;
    }

    return {
      title,
      description,
      start: saveStart,
      end: saveEnd,
      allDay,
      location,
      color,
      calendar: null,
      calendars: [],
      crew: crewId || null,
      eventType: 'event',
      visibility,
      members: visibility === 'selected' ? members : [],
      labels: labels.split(',').map((s) => s.trim()).filter(Boolean),
      notifyMembers,
      project: projectId || null,
      recurrence: {
        freq: recFreq,
        interval: normalizedInterval,
        byWeekday: recurrenceByWeekday,
        // Treat "Until" as inclusive date (end of selected day).
        until: endOfDay(recUntil),
        // Keep existing count if present and no end-date is set; hidden from simplified UI.
        count: recUntil ? null : (recCount ? Number(recCount) : null)
      }
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    const startDate = new Date(start);
    const endDate = new Date(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()) || endDate <= startDate) {
      showToast('Please choose a valid start and end time', 'error');
      return;
    }

    if (crewId && hasCrewConflict) {
      showToast(selectedCrewOption?.reason || 'Selected crew is unavailable for this time', 'error');
      return;
    }

    const payload = buildPayload();
    try {
      let saved;
      if (event) {
        saved = await dispatch(updateEvent({ id: event._id, updates: payload })).unwrap();
      } else {
        saved = await dispatch(createEvent(payload)).unwrap();
      }
      // Backend creates/rebuilds recurrence children on every save.
      // Always refresh events so all occurrences appear immediately.
      await dispatch(fetchEvents()).unwrap();
      onClose();
    } catch (err) {
      console.error('save event failed', err);
    }
  };

  const handleDelete = async () => {
    if (!event) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!event) return;
    await dispatch(deleteEvent(event._id));
    onClose();
  };

  const handleDuplicate = async () => {
    if (!event) return;
    await dispatch(duplicateEvent({ id: event._id }));
    onClose();
  };

  const canDelete = event && (
    role === 'super_admin' || role === 'admin' ||
    event.creator?._id === currentUserId
  );

  const isRecurringSeries = !!(event?.recurrence?.freq && event.recurrence.freq !== 'none');
  const deleteTitle = isRecurringSeries ? 'Delete Recurring Event' : 'Delete Event';
  const deleteMessage = isRecurringSeries
    ? `Are you sure you want to delete "${event?.title || 'this event'}"? This will also delete all repeated occurrences in the series.`
    : `Are you sure you want to delete "${event?.title || 'this event'}"? This action cannot be undone.`;
  const deleteConfirmText = isRecurringSeries ? 'Delete Series' : 'Delete';

  return (
    <>
      <Dialog isOpen={isOpen} onClose={onClose}>
        <form onSubmit={handleSubmit} className="event-dialog">
        <div className="ed-header">
          <h2>{event ? 'Edit Event' : 'New Event'}</h2>
          <button type="button" className="ed-close" onClick={onClose}>×</button>
        </div>

        <div className="ed-body">
          {/* Basics */}
          <div className="ed-section">
            <div className="ed-section-title">Basics</div>
            <div className="ed-row">
              <label>Title *</label>
              <input className="ed-input" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>
            <div className="ed-row">
              <label>Color</label>
              <div className="ed-colors">
                <button
                  type="button"
                  className={`ed-color ed-color-auto ${color === '' ? 'active' : ''}`}
                  onClick={() => setColor('')}
                  title="Auto — use crew color"
                />
                {COLORS.map((c) => (
                  <button
                    type="button"
                    key={c}
                    className={`ed-color ${color === c ? 'active' : ''}`}
                    style={{ background: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
                <label className="ed-color-custom-wrap" title="Pick a custom color">
                  <input
                    type="color"
                    className="ed-color-custom-input"
                    value={color || '#6366F1'}
                    onChange={(e) => setColor(e.target.value)}
                  />
                  <span
                    className={`ed-color ed-color-custom ${color && !COLORS.includes(color) ? 'active' : ''}`}
                    style={{ background: color && !COLORS.includes(color) ? color : 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)' }}
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Project */}
          <div className="ed-section">
            <div className="ed-section-title">Linked Project</div>
            {projectId && projectMeta ? (
              <div className="ed-proj-selected">
                <div className="ed-proj-selected-info">
                  <div className="ed-proj-selected-title">
                    {projectMeta.jobNumber || 'Project'}
                    {projectMeta.client?.name ? ` — ${projectMeta.client.name}` : ''}
                  </div>
                  {projectMeta.address && (
                    <div className="ed-proj-selected-sub">
                      {[projectMeta.address?.street, projectMeta.address?.city, projectMeta.address?.state].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {projectMeta.projectStatus && (
                    <span className={`ed-proj-status status-${projectMeta.projectStatus}`}>
                      {projectMeta.projectStatus.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
                <div className="ed-proj-selected-actions">
                  <button type="button" className="cal-btn" onClick={() => setProjectPickerOpen((v) => !v)}>
                    {projectPickerOpen ? 'Close' : 'Change'}
                  </button>
                  <button type="button" className="cal-btn cal-btn-danger" onClick={() => { setProjectId(''); setProjectMeta(null); }}>
                    Clear
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="cal-btn" onClick={() => setProjectPickerOpen(true)}>
                + Link a project
              </button>
            )}

            {projectPickerOpen && (
              <div className="ed-proj-picker">
                <div className="ed-grid-2">
                  <div>
                    <label>Status</label>
                    <select
                      className="ed-input"
                      value={projectStatus}
                      onChange={(e) => setProjectStatus(e.target.value)}
                    >
                      <option value="">All statuses</option>
                      {PROJECT_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label>Search</label>
                    <input
                      className="ed-input"
                      placeholder="Job #, client, address…"
                      value={projectQuery}
                      onChange={(e) => setProjectQuery(e.target.value)}
                    />
                  </div>
                </div>
                <div className="ed-proj-list">
                  {projectLoading && <div className="ed-proj-empty">Loading…</div>}
                  {!projectLoading && projectOptions.length === 0 && (
                    <div className="ed-proj-empty">No projects match.</div>
                  )}
                  {!projectLoading && projectOptions.map((p) => {
                    const isSelected = String(p._id) === String(projectId);
                    return (
                      <button
                        type="button"
                        key={p._id}
                        className={`ed-proj-row ${isSelected ? 'is-selected' : ''}`}
                        onClick={() => handleSelectProject(p)}
                      >
                        <div className="ed-proj-row-main">
                          <div className="ed-proj-row-title">
                            {p.jobNumber || 'Project'}
                            {p.client?.name ? ` — ${p.client.name}` : ''}
                          </div>
                          {p.address && (
                            <div className="ed-proj-row-sub">
                              {[p.address?.street, p.address?.city, p.address?.state].filter(Boolean).join(', ')}
                            </div>
                          )}
                        </div>
                        {p.projectStatus && (
                          <span className={`ed-proj-status status-${p.projectStatus}`}>
                            {p.projectStatus.replace(/_/g, ' ')}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* When */}
          <div className="ed-section">
            <div className="ed-section-title">When</div>
            <div className="ed-grid-2">
              <div>
                <label>Start *</label>
                <DatePicker
                  selected={start}
                  onChange={setStart}
                  showTimeSelect={!allDay}
                  dateFormat={allDay ? 'MMM d, yyyy' : 'MMM d, yyyy h:mm aa'}
                  className="ed-input"
                  wrapperClassName="ed-datepicker"
                />
              </div>
              <div>
                <label>End *</label>
                <DatePicker
                  selected={end}
                  onChange={setEnd}
                  showTimeSelect={!allDay}
                  dateFormat={allDay ? 'MMM d, yyyy' : 'MMM d, yyyy h:mm aa'}
                  className="ed-input"
                  wrapperClassName="ed-datepicker"
                />
              </div>
            </div>

            <label className="ed-checkbox ed-checkbox-inline">
              <input
                type="checkbox"
                checked={allDay}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAllDay(checked);
                  if (checked) {
                    const s = new Date(start); s.setHours(0, 0, 0, 0); setStart(s);
                    const en = new Date(end); en.setHours(23, 59, 59, 999); setEnd(en);
                  }
                }}
              />
              <span>All-day event</span>
            </label>
          </div>

          {/* Location */}
          <div className="ed-section">
            <div className="ed-section-title">Location</div>
            <div className="ed-row">
              <input
                className="ed-input"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Address, jobsite, meeting point…"
              />
            </div>
          </div>

          {/* Crew */}
          <div className="ed-section">
            <div className="ed-section-title">
              Crew
              {selectedCrewOption && (
                <span className="ed-section-meta">
                  {selectedCrewOption.name}{hasCrewConflict ? ' — conflict' : ''}
                </span>
              )}
            </div>

            {(crews || []).length === 0 ? (
              <div className="ed-help">No crews yet. Add one from the sidebar.</div>
            ) : (
              <>
                <input
                  type="text"
                  className="ed-input"
                  placeholder="Search crews, trades, people…"
                  value={crewSearch}
                  onChange={(e) => setCrewSearch(e.target.value)}
                />

                {hasCrewConflict && (
                  <div className="ed-crew-conflict">
                    {selectedCrewOption?.reason || 'Selected crew is unavailable for this time'}
                  </div>
                )}

                <div className="ed-crew-list">
                  {filteredCrewOptions.length === 0 && (
                    <div className="ed-help" style={{ padding: 8 }}>No crews match.</div>
                  )}
                  {filteredCrewOptions.map((c) => {
                    const isSelected = String(c.id) === String(crewId);
                    const isBlocked = Boolean(c.blocked);
                    const trades = (c.trades || []).filter((t) => t && (t.name || t._id));
                    const people = (c.people || []).filter((p) => p && (p.name || p._id));
                    return (
                      <button
                        type="button"
                        key={c.id}
                        className={`ed-crew-card ${isSelected ? 'is-selected' : ''} ${isBlocked && !isSelected ? 'is-blocked' : ''}`}
                        onClick={() => setCrewId(isSelected ? '' : c.id)}
                        title={c.reason || ''}
                      >
                        <span
                          className="ed-crew-card-dot"
                          style={{ background: c.color || '#6366F1' }}
                        />
                        <div className="ed-crew-card-info">
                          <div className="ed-crew-card-row">
                            <span className="ed-crew-card-name">{c.name}</span>
                            {isBlocked && (
                              <span className="ed-crew-card-busy">Busy</span>
                            )}
                            {isSelected && !isBlocked && (
                              <span className="ed-crew-card-selected">Selected</span>
                            )}
                          </div>
                          {trades.length > 0 && (
                            <div className="ed-crew-card-trades">
                              {trades.map((t) => (
                                <span
                                  key={String(t._id || t)}
                                  className="ed-crew-trade"
                                  title={t.name || ''}
                                >
                                  {t.name || 'Trade'}
                                </span>
                              ))}
                            </div>
                          )}
                          {people.length > 0 && (
                            <div className="ed-crew-card-people">
                              {people.map((p) => {
                                const pTrades = (p.trades || []).filter((t) => t && (t.name || t._id));
                                return (
                                  <div
                                    key={String(p._id || p)}
                                    className="ed-crew-person"
                                  >
                                    <span className="ed-crew-person-name">{p.name || 'Person'}</span>
                                    {pTrades.length > 0 && (
                                      <span className="ed-crew-person-trades">
                                        {pTrades.map((t) => (
                                          <span
                                            key={String(t._id || t)}
                                            className="ed-crew-trade ed-crew-trade-person"
                                          >
                                            {t.name || 'Trade'}
                                          </span>
                                        ))}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Details */}
          <div className="ed-section">
            <div className="ed-section-title">Details</div>
            <div className="ed-row">
              <label>Description / Notes</label>
              <textarea className="ed-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="ed-row">
              <label>Labels (comma separated)</label>
              <input className="ed-input" placeholder="e.g. urgent, repair" value={labels} onChange={(e) => setLabels(e.target.value)} />
            </div>
          </div>

          <div className="ed-section">
            <div className="ed-section-title">Visibility & Invites</div>
            <div className="ed-radios">
              <label className="ed-radio"><input type="radio" checked={visibility === 'all'} onChange={() => setVisibility('all')} /> <span>Everyone</span></label>
              <label className="ed-radio"><input type="radio" checked={visibility === 'selected'} onChange={() => setVisibility('selected')} /> <span>Selected people</span></label>
            </div>
            {visibility === 'selected' && (
              <div className="ed-members">
                {normalizedUsers.map((u) => (
                  <label key={u._id} className="ed-member-row">
                    <input
                      type="checkbox"
                      checked={members.includes(u._id)}
                      onChange={() => toggleMember(u._id)}
                    />
                    <span>{u.displayName} <span className="text-gray-400 text-xs">({normalizeRole(u.roleValue).replace(/_/g, ' ')})</span></span>
                  </label>
                ))}
              </div>
            )}
            <label className="ed-checkbox">
              <input type="checkbox" checked={notifyMembers} onChange={(e) => setNotifyMembers(e.target.checked)} />
              Send email notification
            </label>
          </div>

          <div className="ed-section">
            <div className="ed-section-title">Repeat</div>
            <div className="ed-grid-2">
              <div>
                <label>Frequency</label>
                <select className="ed-input" value={recFreq} onChange={(e) => setRecFreq(e.target.value)}>
                  <option value="none">Does not repeat</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              {recFreq !== 'none' && (
                <div>
                  <label>Every</label>
                  <input
                    type="number"
                    min={1}
                    className="ed-input"
                    value={recInterval}
                    onChange={(e) => setRecInterval(e.target.value)}
                  />
                </div>
              )}
            </div>

            {recFreq !== 'none' && (
              <>
                <div className="ed-help" style={{ marginTop: 6 }}>
                  {recFreq === 'weekly'
                    ? 'Repeats weekly on the event start day.'
                    : 'Repeats every selected number of days.'}
                </div>
                <div className="ed-row" style={{ marginTop: 8 }}>
                <div>
                  <label>Until (optional)</label>
                  <DatePicker
                    selected={recUntil}
                    onChange={setRecUntil}
                    isClearable
                    className="ed-input"
                    dateFormat="MMM d, yyyy"
                  />
                </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="ed-footer">
          <div className="flex gap-2">
            {event && (
              <>
                <button type="button" className="cal-btn" onClick={handleDuplicate}>Duplicate</button>
                {canDelete && <button type="button" className="cal-btn cal-btn-danger" onClick={handleDelete}>Delete</button>}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" className="cal-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="cal-btn cal-btn-primary" disabled={hasCrewConflict}>{event ? 'Save' : 'Create'}</button>
          </div>
        </div>
        </form>
      </Dialog>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title={deleteTitle}
        message={deleteMessage}
        confirmText={deleteConfirmText}
        type="danger"
      />
    </>
  );
};

export default EventDialog;
